import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex, Text, Link, toast, QueryLoader } from "@jsq/ds/core";
import {
  useGetDocumentDetailsQuery,
  useSubscriptionSubmitIdentificationKycMutation,
  DocumentType,
} from "../../graphql/supergraph.index";
import useDocumentProcessor from "../../hooks/documents/useDocumentProcessor";
import { clientContext } from "../../utils/graphql_utils";
import { DefaultModal } from "../common/DefaultModal";
import { textColorNeutral800 } from "../common/style_vanilla.css";
import { DocumentCollector } from "../documents/DocumentCollector";
import { DocumentData } from "../documents/DocumentCollector/types";
import { mapDocumentsToFormProps } from "../documents/DocumentCollectorInvestor/utils";
import { titleStyle } from "./style_vanilla.css";
import { IndividualLandingProps } from "./types";

/**
 * This component provides an interface for users to upload identification documents
 * as required by regulatory requirements. It handles file validation, upload to S3,
 * and document registration in the system.
 *
 * The component will:
 * 1. Allow a user to upload identification documents
 * 3. Upload the document to S3
 * 4. Register the document in the system
 */
export const IndividualLandingScreen: React.FC<IndividualLandingProps> = ({
  caseId,
  profileId,
  basePath,
  showLinkToIraSelector,
  arenaId,
  subscriptionId,
  onStepCompletion,
  subscriptionInReview = false,
}) => {
  const history = useHistory();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);
  const { processDocuments, loading } = useDocumentProcessor(caseId, profileId);
  const [subscriptionSubmitIdentificationKycMutation] =
    useSubscriptionSubmitIdentificationKycMutation();

  const documentDetailsResult = useGetDocumentDetailsQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      profileId: profileId ?? 0,
      caseId: caseId ?? 0,
    },
  });

  /**
   * Handle unsaved changes updates from DocumentCollector
   */
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  /**
   * Handle navigation back with unsaved changes check
   */
  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowBackWarningModal(true);
    } else {
      history.goBack();
    }
  }, [hasUnsavedChanges, history]);

  const handleDocumentProcessing = useCallback(
    async (newFiles: DocumentData[], deleteDocIds: number[]) => {
      try {
        // First process the documents
        await processDocuments(newFiles, deleteDocIds);
        toast.success("Documents updated successfully");

        if (!subscriptionInReview) {
          // Then submit identity verification
          await subscriptionSubmitIdentificationKycMutation({
            variables: {
              subscriptionId,
              arenaId,
            },
          });
        }

        setHasUnsavedChanges(false); // Clear unsaved changes after successful submission
        onStepCompletion();
      } catch (error) {
        toast.error("Error updating documents");
      }
    },
    [
      processDocuments,
      subscriptionSubmitIdentificationKycMutation,
      subscriptionId,
      arenaId,
      onStepCompletion,
      subscriptionInReview,
    ],
  );
  const documentDetails = mapDocumentsToFormProps(
    documentDetailsResult.data?.complianceProfile?.rulesEngine?.documentTypes || [],
    (documentDetailsResult.data?.complianceProfile?.documents as DocumentType[]) || [],
  ).sort((a, b) => a.baseType.localeCompare(b.baseType));

  return (
    <QueryLoader result={documentDetailsResult}>
      <Flex flexDirection="column" gap="20px">
        {showLinkToIraSelector && (
          <Link
            to={`${basePath}/ira-categorization`}
            data-testid="beneficial-owner-selection-back-link"
            variant="muted"
            icon="arrow-left"
            iconPosition="left"
            align="left"
          >
            Select account type
          </Link>
        )}
        <Text variant="h3" className={titleStyle}>
          Identification
        </Text>
        <Text
          variant="regular-md"
          className={textColorNeutral800}
        >{`Due to regulatory requirements, the following documents are required. You can skip this step for now if you don't have these documents available. You will need to return and upload any required documents before signing this subscription.`}</Text>
        <DocumentCollector
          isSubmitting={loading}
          documentDetails={documentDetails}
          onComplete={(newFiles, deleteDocIds) => {
            void handleDocumentProcessing(newFiles, deleteDocIds);
          }}
          onNavigateBack={handleNavigateBack}
          onUnsavedChangesChange={handleUnsavedChangesChange}
        />
      </Flex>
      <DefaultModal
        show={showBackWarningModal}
        title="Warning"
        body="You have unsaved changes. Are you sure you want to go back without submitting?"
        negBtnText="Cancel"
        posBtnText="Back"
        posBtnVariant="danger"
        onClose={() => setShowBackWarningModal(false)}
        onConfirm={() => {
          setShowBackWarningModal(false);
          setHasUnsavedChanges(false);
          history.goBack();
        }}
      />
    </QueryLoader>
  );
};
